import HeaderActionsMenu from '@/components/common/HeaderActionsMenu';
import { Settings2 } from 'lucide-react';
import { usePageTitle } from '@/layouts/usePageTitle';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchInverterDetail, type InverterDetail } from '@/api/inverterDetail';
import { fetchInverterSolarPanelSummary, type SolarPanelSummary } from '@/api/inverters';
import InverterDetailCard from '@/components/inverters/InverterDetailCard';
import InverterPowerChart from '@/components/inverters/InverterPowerChart';
import { useI18n } from '@/i18n';
import SolarPanelInfoCard from '@/components/solar-panels/SolarPanelInfoCard';
import StringPanelEditor from '@/components/solar-panels/StringPanelEditor';
import { useErrorToast, LoadRetry } from '@/lib/notify';

export default function InverterDetailPage() {
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<InverterDetail | null>(null);
  const [solarPanelSummary, setSolarPanelSummary] = useState<SolarPanelSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [solarPanelLoading, setSolarPanelLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useErrorToast(error);
  const [editing,setEditing]=useState(false);
  const [revision,setRevision]=useState(0);
  const current = data?.current;
  usePageTitle(current && Number(current.inverter_id) === Number(id)
    ? `${current.inverter_power_plant_name} - İnverter - ${current.inverter_name}`
    : 'İnverter');

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setError(null);
    fetchInverterDetail(Number(id))
      .then(value => {if(!cancelled)setData(value);})
      .catch(() => {if(!cancelled)setError(t('inverterDetail.loadError'));})
      .finally(() => {if(!cancelled)setLoading(false);});

    setSolarPanelLoading(true);
    setSolarPanelSummary(null);
    fetchInverterSolarPanelSummary(Number(id))
      .then(value => {if(!cancelled)setSolarPanelSummary(value);})
      .catch(() => {if(!cancelled)setSolarPanelSummary(null);})
      .finally(() => {if(!cancelled)setSolarPanelLoading(false);});
    return () => {cancelled=true;};
  }, [id, t,revision]);

  if (loading) return <div className="p-6 text-sm text-gray-500">{t('common.loading')}</div>;
  if (error) return <LoadRetry />;
  if (!data) return <div className="p-6 text-sm text-rose-500">{t('inverterDetail.notFound')}</div>;

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-4 overflow-y-auto p-4 pb-6">
      <HeaderActionsMenu label="İnverter işlemleri" items={[{label:'String Panel Yapılandırması',icon:<Settings2 size={15} className="shrink-0 text-blue-600"/>,onSelect:()=>setEditing(true)}]}/>


      <InverterDetailCard
        current={data.current}
        strings={data.strings}
      />
      {!solarPanelLoading && Boolean(solarPanelSummary?.length) && <SolarPanelInfoCard
        title="Inverter Solar Panel Details"
        data={solarPanelSummary}
        loading={solarPanelLoading}
      />}
      <InverterPowerChart
        inverterId={Number(id)}
        hourly={data.hourly}
        daily={data.daily}
        monthly={data.monthly}
        yearly={data.yearly}
      />
      {editing && <StringPanelEditor key={id} inverterId={Number(id)} onClose={()=>setEditing(false)} onSaved={()=>setRevision(value=>value+1)}/>}
    </div>
  );
}
